import React, { useState, useEffect } from 'react';
import { Globe, Mail, Phone, MapPin, ChevronRight, X, Check, Users, Maximize, Bed, Sparkles, Star } from 'lucide-react';

export default function Rooms() {
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [language, setLanguage] = useState('fr');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const rooms = [
    {
      id: 1,
      name: { fr: 'Chambre Simple', en: 'Simple Room' },
      price: 30,
      promoFrom: { fr: 'À partir de 30 €', en: 'From €30' },
      area: 15,
      type: { fr: '1 personne', en: '1 person' },
      shortDesc: {
        fr: "Petite chambre cosy avec accès direct à la terrasse et vue panoramique.",
        en: "Cozy small room with direct terrace access and panoramic view.",
      },
      image: 'img/image2.jpeg',
      details: {
        quick: { 
          fr: ['15 m²', 'Vue sur les montagnes', '1 Personne', '1 Lit double', '1 Salle de bain'], 
          en: ['15 m²', 'Mountain view', '1 Person', '1 Double bed', '1 Bathroom'] 
        },
        fullDesc: {
          fr: "Chambre cosy pour une personne, offrant une vue splendide sur les montagnes, parfaite pour un séjour reposant.",
          en: "Cozy room for one person with stunning mountain views, perfect for a relaxing stay."
        },
        bedding: { fr: ['Lit double'], en: ['Double bed'] },
        amenities: {
          fr: ['Climatiseur Réversible', 'Armoire', 'Wifi & Internet', 'Serviettes Éponges', 'Sèche Cheveux', 'Savonnette, Shampoing et Gel Douche'],
          en: ['Reversible AC', 'Wardrobe', 'Wifi & Internet', 'Towels', 'Hairdryer', 'Soap, Shampoo & Shower gel']
        },
        included: {
          fr: ['Lit grand confort : 160x200 cm', 'Draps et housses de couettes en percale', "Bouteille d'eau", 'Salle de bain avec douche', 'Toilettes privatives'],
          en: ['Comfort bed: 160x200 cm', 'Percale sheets & duvet covers', 'Bottled water', 'Bathroom with shower', 'Private WC']
        },
        pricing: {
          standard: { fr: '30 € / nuit en BB (1 personne)', en: '€30 / night BB (1 person)' },
          highSeason: { fr: '40 € / nuit en DP (1 personne)', en: '€40 / night HP (1 person)' },
          touristTax: { fr: 'Taxe de séjour 1€ / nuit', en: 'Tourist tax €1 / night' },
        }
      }
    },
    {
      id: 2,
      name: { fr: 'Chambre Double', en: 'Double Room' },
      price: 40,
      promoFrom: { fr: 'À partir de 40 €', en: 'From €40' },
      area: 20,
      type: { fr: '2 personnes', en: '2 people' },
      shortDesc: {
        fr: "Chambre double avec vue sur les montagnes, parfaite pour un séjour calme et ressourçant en pleine nature.",
        en: "Double room with mountain views, perfect for a calm and rejuvenating stay in nature."
      },
      image: 'img/img1.png',
      details: {
        quick: { 
          fr: ['20 m²', 'Vue sur les montagnes', '2 Personnes', '1 Grand Lit', '1 Salle de bain'], 
          en: ['20 m²', 'Mountain view', '2 People', '1 Large bed', '1 Bathroom'] 
        },
        fullDesc: { 
          fr: "Chambre double confortable pour deux personnes, offrant une vue imprenable sur les montagnes. Idéale pour un séjour paisible et reposant au cœur de la nature.", 
          en: "Comfortable double room for two people with stunning mountain views. Ideal for a peaceful stay in the heart of nature." 
        },
        bedding: { fr: ['Lit double', 'Canapé'], en: ['Double bed', 'Sofa'] },
        amenities: { 
          fr: ['Climatiseur Réversible', 'Armoire', 'Wifi & Internet', 'Serviettes Éponges', 'Sèche Cheveux', 'Savonnette, Shampoing et Gel Douche'], 
          en: ['Reversible AC', 'Wardrobe', 'Wifi & Internet', 'Towels', 'Hairdryer', 'Soap, Shampoo & Shower gel'] 
        },
        included: { 
          fr: ['Petit-déjeuner inclus', 'Service en chambre disponible'], 
          en: ['Breakfast included', 'Room service available'] 
        },
        pricing: {
          standard: { fr: '40 € / nuit en BB (2 personnes)', en: '€40 / night BB (2 people)' },
          highSeason: { fr: '60 € / nuit en DP (2 personnes)', en: '€60 / night HP (2 people)' },
          discounts: { fr: 'Taxe de séjour 1 € /nuit/personne', en: 'Tourist tax €1 /night/person' }
        }
      }
    },
    {
      id: 3,
      name: { fr: 'Chambre Triple', en: 'Triple Room' },
      price: 50,
      promoFrom: null,
      area: 25,
      type: { fr: '3 personnes', en: '3 people' },
      shortDesc: {
        fr: 'Chambre triple confortable avec vue sur les montagnes, idéale pour un séjour détente en famille ou entre amis.',
        en: 'Comfortable triple room with mountain views, ideal for a relaxing stay with family or friends.'
      },
      image: 'img/image12.jpeg',
      details: {
        quick: { 
          fr: ['25 m²', 'Vue sur les montagnes', '3 Personnes', '1 Grand Lit + 1 Lit simple', '1 Salle de bain'], 
          en: ['25 m²', 'Mountain view', '3 People', '1 Large bed + 1 Single bed', '1 Bathroom'] 
        },
        fullDesc: { 
          fr: 'Chambre triple/twin confortable, idéale pour trois personnes, offrant une vue agréable sur les montagnes. Parfaite pour un séjour reposant en famille ou entre amis', 
          en: 'Comfortable triple/twin room, ideal for three people with pleasant mountain views. Perfect for a relaxing stay with family or friends.' 
        },
        bedding: { fr: ['Lit double', 'Lit simple'], en: ['Double bed', 'Single bed'] },
        amenities: { 
          fr: ['Climatiseur Réversible', 'Armoire', 'Wifi & Internet', 'Serviettes Éponges', 'Sèche Cheveux', 'Savonnette, Shampoing et Gel Douche'], 
          en: ['Reversible AC', 'Wardrobe', 'Wifi & Internet', 'Towels', 'Hairdryer', 'Soap, Shampoo & Shower gel'] 
        },
        included: { 
          fr: ['Linge de maison premium', 'Petit-déjeuner inclus'], 
          en: ['Premium linens', 'Breakfast included'] 
        },
        pricing: {
          standard: { fr: '50 € / nuit en BB (3 personnes)', en: '€50 / night BB (3 people)' },
          highSeason: { fr: '80 € / nuit en DP (3 personnes)', en: '€80 / night HP (3 people)' },
          discounts: { fr: 'Taxe de séjour 1 € /nuit/personne', en: 'Tourist tax €1 /night/person' }
        }
      }
    },
  ];

  const t = {
    fr: {
      title: 'Nos Chambres',
      subtitle: 'Un Séjour Inoubliable aux pieds des montagnes',
      heroDesc: 'Choisissez la chambre qui correspondra le mieux à votre séjour au Riad Lina.',
      viewMore: 'Voir Plus',
      close: 'Fermer',
      reserve: 'Réserver',
      perNight: 'par nuit',
      description: 'Description',
      bedding: 'Information Literie',
      amenities: 'Équipements',
      included: 'Inclus',
      pricingInfo: 'Informations Prix',
      contactTitle: 'Nos Chambres',
      reserveNow: 'Réserver Maintenant',
      contact: {
        address: 'TANDILT-AMMELNE-TAFROUT',
        phone: '+212 667 624183',
        email: 'CONTACT@RIADLINA.MA'
      }
    },
    en: {
      title: 'Our Rooms & Suites',
      subtitle: 'An Unforgettable Stay at the Gateway to the Desert',
      heroDesc: 'Choose the room or suite that best suits your stay at Riad Lina.',
      viewMore: 'View More',
      close: 'Close',
      reserve: 'Book Now',
      perNight: 'per night',
      description: 'Description',
      bedding: 'Bedding Information',
      amenities: 'Amenities',
      included: 'Included',
      pricingInfo: 'Pricing Information',
      contactTitle: 'Our Rooms',
      reserveNow: 'Book Now',
      contact: {
        address: 'TANDILT-AMMELNE-TAFROUT',
        phone: '+212 667 624183',
        email: 'CONTACT@RIADLINA.MA'
      }
    }
  }[language];

  const handleReserve = () => {
    window.scrollTo({ top: document.getElementById('contact').offsetTop, behavior: 'smooth' });
    setSelectedRoom(null);
  };

  return (
    <div style={{ fontFamily: 'Georgia, serif', background: '#0a0a0a', color: '#fff', minHeight: '100vh' }}>
      <style>{`
        * { 
          margin: 0; 
          padding: 0; 
          box-sizing: border-box; 
        }
        
        body { 
          font-family: 'Cormorant Garamond', serif;
          overflow-x: hidden;
        }
        
        .luxury-card {
          background: linear-gradient(145deg, #1a1a1a, #0d0d0d);
          border: 1px solid rgba(212, 175, 55, 0.2);
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
        }
        
        .luxury-card:hover {
          transform: translateY(-15px);
          border-color: rgba(212, 175, 55, 0.6);
          box-shadow: 0 30px 70px rgba(212, 175, 55, 0.3);
        }
        
        .room-img-container {
          position: relative;
          overflow: hidden;
          height: 320px;
        }
        
        .room-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        
        .luxury-card:hover .room-img {
          transform: scale(1.1);
        }
        
        .price-badge {
          position: absolute;
          top: 20px;
          right: 20px;
          background: linear-gradient(135deg, #d4af37, #f9e79f);
          color: #000;
          padding: 12px 20px;
          border-radius: 30px;
          font-weight: 700;
          box-shadow: 0 5px 20px rgba(0,0,0,0.3);
          z-index: 10;
        }
        
        .promo-badge {
          position: absolute;
          top: 20px;
          left: 20px;
          background: rgba(212, 175, 55, 0.95);
          color: #000;
          padding: 8px 16px;
          border-radius: 20px;
          font-weight: 600;
          font-size: 14px;
          z-index: 10;
        }
        
        .gold-line {
          background: linear-gradient(90deg, transparent, #d4af37, transparent);
          height: 2px;
        }
        
        .text-gradient {
          background: linear-gradient(135deg, #d4af37 0%, #f9e79f 50%, #d4af37 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.95);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          overflow-y: auto;
        }
        
        .modal-content {
          background: linear-gradient(145deg, #1a1a1a, #0d0d0d);
          border: 1px solid rgba(212, 175, 55, 0.3);
          border-radius: 20px;
          max-width: 1100px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          animation: slideUp 0.4s ease-out;
          position: relative;
        }
        
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(50px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .contact-btn {
          transition: all 0.3s ease;
        }

        .contact-btn:hover {
          transform: scale(1.05);
          box-shadow: 0 15px 40px rgba(212, 175, 55, 0.5);
        }

        @media (max-width: 768px) {
          .modal-content {
            margin: 20px 0;
          }
        }
      `}</style>

      {/* Hero Section */}
      <section style={{
        position: 'relative',
        height: '100vh',
        backgroundImage: "url('img/image11.jpeg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0.8))'
        }} />
        
        <div style={{
          position: 'relative',
          zIndex: 10,
          textAlign: 'center',
          width: '90%',
          maxWidth: '1000px',
          padding: '0 20px'
        }}>
          <h1 style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: 'clamp(40px, 8vw, 100px)',
            fontWeight: 900,
            marginBottom: '30px',
            letterSpacing: '4px',
            textShadow: '3px 3px 30px rgba(0,0,0,0.8)'
          }} className="text-gradient">
            {t.title}
          </h1>
          <p style={{
            fontSize: 'clamp(20px, 4vw, 36px)',
            marginBottom: '40px',
            fontFamily: 'Playfair Display, serif',
            fontStyle: 'italic',
            color: '#d4af37',
            textShadow: '2px 2px 15px rgba(0,0,0,0.7)'
          }}>
            {t.subtitle}
          </p>
          <div className="gold-line" style={{ width: '200px', margin: '0 auto 50px' }} />
          <p style={{
            fontSize: 'clamp(16px, 2.5vw, 22px)',
            lineHeight: '1.8',
            maxWidth: '800px',
            margin: '0 auto',
            color: '#e0e0e0',
            textShadow: '1px 1px 10px rgba(0,0,0,0.8)'
          }}>
            {t.heroDesc}
          </p>
        </div>
      </section>

      {/* Rooms Grid */}
      <section style={{ 
        padding: '100px 10px', 
        background: '#0f0f0f',
        position: 'relative'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '40px',
            justifyItems: 'center'
          }}>
            {rooms.map((room) => (
              <div 
                key={room.id} 
                className="luxury-card" 
                onClick={() => setSelectedRoom(room)} 
                style={{
                  borderRadius: '20px',
                  overflow: 'hidden',
                  width: '90%',
                  maxWidth: '420px'
                }}
              >
                <div className="room-img-container">
                  <img src={room.image} alt={room.name[language]} className="room-img" />
                  <div className="price-badge">
                    <div style={{ fontSize: '26px', fontFamily: 'Playfair Display, serif' }}>
                      {room.price}€
                    </div>
                    <div style={{ fontSize: '12px', fontWeight: 400 }}>
                      {t.perNight}
                    </div>
                  </div>
                  {room.promoFrom && (
                    <div className="promo-badge">
                      {room.promoFrom[language]}
                    </div>
                  )}
                </div>

                <div style={{ padding: '35px' }}>
                  <h3 style={{
                    fontFamily: 'Playfair Display, serif',
                    fontSize: '32px',
                    color: '#d4af37',
                    fontWeight: 700,
                    marginBottom: '15px'
                  }}>
                    {room.name[language]}
                  </h3>

                  <p style={{
                    fontSize: '17px',
                    color: '#aaa',
                    lineHeight: '1.7',
                    marginBottom: '25px',
                    minHeight: '80px'
                  }}>
                    {room.shortDesc[language]}
                  </p>

                  <div style={{
                    display: 'flex',
                    gap: '15px',
                    marginBottom: '25px',
                    paddingTop: '25px',
                    borderTop: '1px solid rgba(212, 175, 55, 0.2)',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'rgba(212, 175, 55, 0.1)',
                      padding: '10px 15px',
                      borderRadius: '10px',
                      color: '#d4af37'
                    }}>
                      <Maximize size={18} />
                      <span style={{ fontSize: '15px', fontWeight: 600 }}>{room.area} m²</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'rgba(212, 175, 55, 0.1)',
                      padding: '10px 15px',
                      borderRadius: '10px',
                      color: '#d4af37'
                    }}>
                      <Users size={18} />
                      <span style={{ fontSize: '15px', fontWeight: 600 }}>{room.type[language]}</span>
                    </div>
                  </div>

                  <button 
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: 'linear-gradient(135deg, #d4af37, #f9e79f)',
                      color: '#000',
                      border: 'none',
                      borderRadius: '50px',
                      cursor: 'pointer',
                      fontSize: '18px',
                      fontWeight: 700,
                      letterSpacing: '1px',
                      transition: 'all 0.3s',
                      fontFamily: 'Playfair Display, serif',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px'
                    }} 
                    onMouseEnter={e => { e.target.style.transform = 'scale(1.05)'; }}
                    onMouseLeave={e => { e.target.style.transform = 'scale(1)'; }}
                  >
                    {t.viewMore}
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      {selectedRoom && (
        <div className="modal-overlay" onClick={() => setSelectedRoom(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={{ position: 'relative' }}>
              <img
                src={selectedRoom.image}
                alt={selectedRoom.name[language]}
                style={{
                  width: '100%',
                  height: '400px',
                  objectFit: 'cover',
                  borderRadius: '20px 20px 0 0'
                }}
              />
              <button
                onClick={() => setSelectedRoom(null)}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: 'rgba(0, 0, 0, 0.7)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '50px',
                  height: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  zIndex: 100
                }}
                onMouseEnter={e => e.target.style.background = 'rgba(212, 175, 55, 0.9)'}
                onMouseLeave={e => e.target.style.background = 'rgba(0, 0, 0, 0.7)'}
              >
                <X size={24} color="#fff" />
              </button>
            </div>

            <div style={{ padding: '50px 30px' }}>
              <div style={{ marginBottom: '40px' }}>
                <h2 style={{
                  fontFamily: 'Playfair Display, serif',
                  fontSize: 'clamp(32px, 5vw, 42px)',
                  color: '#d4af37',
                  fontWeight: 900,
                  marginBottom: '20px'
                }}>
                  {selectedRoom.name[language]}
                </h2>
                <div style={{
                  display: 'flex',
                  gap: '15px',
                  alignItems: 'center',
                  flexWrap: 'wrap'
                }}>
                  {selectedRoom.details.quick[language].map((item, i) => (
                    <span key={i} style={{
                      background: 'rgba(212, 175, 55, 0.1)',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      fontSize: '14px',
                      color: '#d4af37',
                      fontWeight: 600
                    }}>
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '40px',
                marginBottom: '40px'
              }}>
                <div>
                  <h3 style={{
                    fontSize: '24px',
                    color: '#d4af37',
                    marginBottom: '20px',
                    fontFamily: 'Playfair Display, serif',
                    fontWeight: 700
                  }}>
                    {t.description}
                  </h3>
                  <p style={{ fontSize: '17px', color: '#bbb', lineHeight: '1.8', marginBottom: '30px' }}>
                    {selectedRoom.details.fullDesc[language]}
                  </p>

                  <h4 style={{
                    fontSize: '20px',
                    color: '#d4af37',
                    marginBottom: '15px',
                    fontWeight: 600
                  }}>
                    {t.bedding}
                  </h4>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {selectedRoom.details.bedding[language].map((item, i) => (
                      <li key={i} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginBottom: '10px',
                        color: '#bbb',
                        fontSize: '16px'
                      }}>
                        <Bed size={18} color="#d4af37" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 style={{
                    fontSize: '20px',
                    color: '#d4af37',
                    marginBottom: '15px',
                    fontWeight: 600
                  }}>
                    {t.amenities}
                  </h4>
                  <ul style={{ listStyle: 'none', padding: 0, marginBottom: '30px' }}>
                    {selectedRoom.details.amenities[language].map((item, i) => (
                      <li key={i} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginBottom: '10px',
                        color: '#bbb',
                        fontSize: '16px'
                      }}>
                        <Check size={18} color="#d4af37" />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <h4 style={{
                    fontSize: '20px',
                    color: '#d4af37',
                    marginBottom: '15px',
                    fontWeight: 600
                  }}>
                    {t.included}
                  </h4>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {selectedRoom.details.included[language].map((item, i) => (
                      <li key={i} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginBottom: '10px',
                        color: '#bbb',
                        fontSize: '16px'
                      }}>
                        <Sparkles size={18} color="#d4af37" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div style={{
                background: 'rgba(212, 175, 55, 0.1)',
                padding: '30px',
                borderRadius: '15px',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                marginBottom: '30px'
              }}>
                <h4 style={{
                  fontSize: '22px',
                  color: '#d4af37',
                  marginBottom: '20px',
                  fontFamily: 'Playfair Display, serif',
                  fontWeight: 700
                }}>
                  {t.pricingInfo}
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <div style={{ fontSize: '16px', color: '#ccc' }}>
                    <strong>Tarif standard:</strong> {selectedRoom.details.pricing.standard[language]}
                  </div>
                  {selectedRoom.details.pricing.highSeason && (
                    <div style={{ fontSize: '16px', color: '#ccc' }}>
                      <strong>Haute saison:</strong> {selectedRoom.details.pricing.highSeason[language]}
                    </div>
                  )}
                  {selectedRoom.details.pricing.discounts && (
                    <div style={{ fontSize: '16px', color: '#ccc' }}>
                      {selectedRoom.details.pricing.discounts[language]}
                    </div>
                  )}
                  {selectedRoom.details.pricing.touristTax && (
                    <div style={{ fontSize: '14px', color: '#888', marginTop: '10px' }}>
                      ℹ️ {selectedRoom.details.pricing.touristTax[language]}
                    </div>
                  )}
                </div>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '20px',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  {[1, 2, 3, 4, 5].map(i => (
                    <Star key={i} size={24} fill="#d4af37" color="#d4af37" />
                  ))}
                </div>
                <button 
                  style={{
                    background: 'linear-gradient(135deg, #d4af37, #f9e79f)',
                    color: '#000',
                    padding: '18px 50px',
                    fontSize: '20px',
                    border: 'none',
                    borderRadius: '50px',
                    cursor: 'pointer',
                    fontWeight: 700,
                    letterSpacing: '2px',
                    transition: 'all 0.3s',
                    fontFamily: 'Playfair Display, serif',
                    boxShadow: '0 10px 40px rgba(212, 175, 55, 0.4)'
                  }} 
                  onClick={handleReserve}
                  onMouseEnter={e => { 
                    e.target.style.transform = 'scale(1.05)'; 
                    e.target.style.boxShadow = '0 15px 50px rgba(212, 175, 55, 0.6)'; 
                  }}
                  onMouseLeave={e => { 
                    e.target.style.transform = 'scale(1)'; 
                    e.target.style.boxShadow = '0 10px 40px rgba(212, 175, 55, 0.4)'; 
                  }}
                >
                  {t.reserve}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Section */}
      <section id="contact" style={{
        padding: '120px 30px',
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        color: '#fff',
        position: 'relative'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{
            fontFamily: 'Playfair Display, serif', 
            fontSize: 'clamp(36px, 5vw, 56px)',
            marginBottom: '30px', 
            fontWeight: 700,
            color: '#d4af37'
          }}>
            {t.contactTitle}
          </h2>
          <div style={{ width: '100px', height: '3px', background: '#d4af37', margin: '0 auto 60px' }} />
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '40px', 
            marginBottom: '50px' 
          }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: '15px',
              padding: '20px'
            }}>
              <MapPin size={40} color="#d4af37" />
              <h3 style={{ 
                fontFamily: 'Playfair Display, serif', 
                fontSize: '20px', 
                color: '#d4af37',
                marginBottom: '10px'
              }}>
                Adresse
              </h3>
              <p style={{ fontSize: '16px', color: '#ccc', textAlign: 'center' }}>
                {t.contact.address}
              </p>
            </div>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: '15px',
              padding: '20px'
            }}>
              <Phone size={40} color="#d4af37" />
              <h3 style={{ 
                fontFamily: 'Playfair Display, serif', 
                fontSize: '20px', 
                color: '#d4af37',
                marginBottom: '10px'
              }}>
                Téléphone
              </h3>
              <p style={{ fontSize: '16px', color: '#ccc' }}>
                {t.contact.phone}
              </p>
            </div>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: '15px',
              padding: '20px'
            }}>
              <Mail size={40} color="#d4af37" />
              <h3 style={{ 
                fontFamily: 'Playfair Display, serif', 
                fontSize: '20px', 
                color: '#d4af37',
                marginBottom: '10px'
              }}>
                Email
              </h3>
              <p style={{ fontSize: '16px', color: '#ccc' }}>
                {t.contact.email}
              </p>
            </div>
          </div>

          <button 
            className="contact-btn"
            style={{
              background: 'linear-gradient(135deg, #d4af37, #f9e79f)',
              color: '#000', 
              padding: '18px 50px', 
              fontSize: '18px',
              border: 'none', 
              borderRadius: '50px', 
              cursor: 'pointer',
              fontWeight: 700, 
              letterSpacing: '2px',
              fontFamily: 'Playfair Display, serif',
              boxShadow: '0 10px 30px rgba(212, 175, 55, 0.3)'
            }}
          >
            {t.reserveNow}
          </button>
        </div>
      </section>
    </div>
  );
}